import { OperationType } from '@aavaz-ai/eql-parser-ts/dist/types';
import { EMPTY_ARRAY, EqlFieldName } from '@modules/common/constants';
import { QuantifyFeedbackListDrawer } from '@modules/quantify/components/QuantifyFeedbackListDrawer';
import { QuantifyReportTable } from '@modules/quantify/components/QuantifyReportTableWidget/QuantifyReportTable';
import { ShowFeedbackDrawerFromTableOptions } from '@modules/quantify/components/QuantifyReportTableWidget/types';
import { useGetQuantifyReportTableColumns } from '@modules/quantify/hooks';
import { useFetchQuantifyTableData } from '@modules/quantify/hooks/useFetchQuantifyTableData';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getCompareQueryContainer,
  getQuantifyQueryContainer,
} from '@modules/quantify/utils';
import { ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material';
import { Box } from '@mui/material';
import { DataGridPro, GridRowId } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToggle } from 'react-use';
import { TimeRange } from 'v2/core/entities';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';

export function AggregateFormulaResultTable() {
  const [feedbackQueryContainer, setFeedbackQueryContainer] =
    useState<QueryContainer | null>(null);
  const query = useQuantifyStore(({ query }) => query);
  const [shouldOpenFeedbackDrawer, toggleShouldOpenFeedbackDrawer] =
    useToggle(false);
  const [selectedMetricKeyId, setSelectedMetricKeyId] = useState('');
  const [selectedMetricKeyLabel, setSelectedMetricKeyLabel] = useState('');
  const [isSelectedCompareFieldA, setIsSelectedCompareFieldA] = useState(false);
  const whereClause = useQuantifyStore(
    ({ query }) => query?.whereClause ?? EmptyWhereClause,
  );
  const [isSelectedMetricKeyInvisible, setIsSelectedMetricKeyInvisible] =
    useState(false);
  const [
    isSelectedMetricKeyUpdateInProgress,
    setIsSelectedMetricKeyUpdateInProgress,
  ] = useState(false);

  const compareTo = useQuantifyStore(({ query }) => query.compareQuery);
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const formulaID = useQuantifyStore(
    ({ query }) => query.formulaQuery?.eqlFunction,
  );
  const [fetchQuantifyTableData] = useFetchQuantifyTableData({
    shouldFetchCount: true,
  });
  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const aggregateFormulaResponse = useQuantifyStore(({ reportData }) =>
    compareTo
      ? reportData.compare.aggregateFormulaResponse
      : reportData.quantify.aggregateFormulaResponse,
  );
  const isTableQueryInProgress = useQuantifyStore(
    ({ visual }) => visual.isTableQueryInProgress && !aggregateFormulaResponse,
  );
  const tableRows = useMemo(
    () => (aggregateFormulaResponse ? [aggregateFormulaResponse] : EMPTY_ARRAY),
    [aggregateFormulaResponse],
  );

  const [detailPanelExpandedRowIDs, setDetailPanelExpandedRowIDs] = useState<
    GridRowId[]
  >([]);
  const shouldExpandDetailPanel = !!detailPanelExpandedRowIDs.length;

  useEffect(() => {
    setDetailPanelExpandedRowIDs([]);
  }, [tableRows]);

  useEffect(() => {
    if (shouldExpandDetailPanel) {
      dispatch({
        type: QuantifyActionType.SET_REPORT_DATA,
        payload: {
          quantify: { tableRows: null },
          compare: { tableRows: null },
        },
      });
      fetchQuantifyTableData();
    }
  }, [shouldExpandDetailPanel, fetchQuantifyTableData, dispatch]);

  const openFeedbackDrawer = (params: ShowFeedbackDrawerFromTableOptions) => {
    toggleShouldOpenFeedbackDrawer(true);
    setSelectedMetricKeyId(params.id);
    setSelectedMetricKeyLabel(params.label);
    setIsSelectedCompareFieldA(params.isCompareFieldA ?? false);
    setIsSelectedMetricKeyInvisible(params.isInvisible);
    setIsSelectedMetricKeyUpdateInProgress(params.isUpdateInProgress);

    const getQueryContainer = () => {
      if (query.compareQuery) {
        return getCompareQueryContainer({
          query,
          isFieldA: params.isCompareFieldA ?? false,
          metricKeyId: params.id,
          metricCategory: params.category,
        });
      }

      return getQuantifyQueryContainer({
        query,
        metricKeyId: params.id,
        metricCategory: params.category,
        dateRange: params.dateRange ?? (query.duration as TimeRange),
        whereClause:
          params.whereClause && whereClause
            ? combineWhereClauses(
                whereClause,
                params.whereClause,
                OperationType.AND,
              )
            : whereClause || params.whereClause,
      });
    };

    const queryContainer = getQueryContainer();
    setFeedbackQueryContainer(queryContainer);
  };

  const { columns } = useGetQuantifyReportTableColumns({
    formulaID: formulaID,
    shouldHideActions: true,
    onFeedbackCountClick: openFeedbackDrawer,
  });

  const getDetailPanelContent = useCallback(() => {
    if (!shouldExpandDetailPanel) {
      return <></>;
    }

    return (
      <QuantifyReportTable
        shouldHideToolbar={true}
        shouldBlockSelection={true}
        shouldHideActions={true}
        isCountTable={true}
        pinnedColumns={{}}
      />
    );
  }, [shouldExpandDetailPanel]);

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  const pinnColums = columns.length > 6;

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 150,
        // Required to show loader
        height: isTableQueryInProgress || !tableRows.length ? 150 : undefined,
      }}
    >
      <DataGridPro
        hideFooter
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        loading={isTableQueryInProgress}
        columns={columns}
        rows={tableRows}
        pinnedColumns={{
          left: [pinnColums ? 'value' : ''],
        }}
        getDetailPanelHeight={getDetailPanelHeight}
        getDetailPanelContent={
          groupBy?.field === EqlFieldName.FeedbackRecords
            ? undefined
            : getDetailPanelContent
        }
        detailPanelExpandedRowIds={detailPanelExpandedRowIDs}
        slots={{
          detailPanelExpandIcon: ExpandMoreRounded,
          detailPanelCollapseIcon: ExpandLessRounded,
        }}
        onDetailPanelExpandedRowIdsChange={(ids) => {
          setDetailPanelExpandedRowIDs(ids);
        }}
      />
      {feedbackQueryContainer && (
        <QuantifyFeedbackListDrawer
          open={shouldOpenFeedbackDrawer}
          onClose={toggleShouldOpenFeedbackDrawer}
          queryContainer={feedbackQueryContainer}
          metricKeyLabel={selectedMetricKeyLabel}
          metricKeyId={selectedMetricKeyId}
          metricKeyCategory={undefined}
          isCompareFieldA={isSelectedCompareFieldA}
          isMetricKeyInvisible={isSelectedMetricKeyInvisible}
          isMetricKeyUpdateInProgress={isSelectedMetricKeyUpdateInProgress}
        />
      )}
    </Box>
  );
}
